import { useCallback, useEffect, useState } from 'react';
import { getFileFootprint, PhotoValidationStatus } from '@frontend/ui-elements';
import { FILE_MANAGER_FORMATS_VALIDABLE } from '@customer-platform-shared/components/Forms/FileManagerForm/FileManagerForm.const';
import { FileManagerFormValidationError } from '@customer-platform-shared/components/Forms/FileManagerForm/FileManagerForm.types';
import { useIntl } from 'react-intl';
import { UseMutateAsyncFunction } from '@tanstack/react-query';
import { AxiosError, AxiosResponse } from 'axios';
import {
  ISellerOrderPhotoValidationError,
  ISellerOrderPhotoValidationSubmissionData,
} from '@frontend/api';

interface UseFileValidationOptions {
  onValidationChange?: () => void;
  errorResponseFieldName: string;
  photoValidator?: UseMutateAsyncFunction<
    AxiosResponse,
    AxiosError<ISellerOrderPhotoValidationError>,
    ISellerOrderPhotoValidationSubmissionData
  >;
}

export function useImageValidationStatus({
  onValidationChange,
  errorResponseFieldName,
  photoValidator,
}: UseFileValidationOptions) {
  const intl = useIntl();
  const [validationStatus, setValidationStatus] = useState<
    Map<string, PhotoValidationStatus>
  >(new Map());

  const validateFiles = (files: File[]) => {
    if (files.length < 1) {
      return;
    }
    files.forEach(file => {
      updateValidationStatus(file, 'pending');
    });
    if (!photoValidator) {
      handleValidationError(undefined, files);
      return;
    }
    photoValidator({ sellerOrderPhotos: files })
      .then(() => {
        files.forEach(file => updateValidationStatus(file, 'success'));
      })
      .catch(error => {
        handleValidationError(error, files);
      });
  };

  const handleValidationError = (
    error: FileManagerFormValidationError | undefined,
    files: File[],
  ) => {
    if (
      error &&
      error.response?.data?.[errorResponseFieldName] &&
      !(typeof error.response.data[errorResponseFieldName] === 'string')
    ) {
      const receivedFiles = files;
      for (const errorFileIndex in error.response.data[
        errorResponseFieldName
      ]) {
        const errorData =
          error.response.data[errorResponseFieldName][errorFileIndex];
        // Under some circumstances, this can be a string
        if (typeof errorData === 'string') {
          updateValidationStatus(
            receivedFiles[+errorFileIndex],
            'error',
            errorData,
          );
          continue;
        }
        // For zip & pdf files, gather the nested errors and display in a single error message
        if (Object.keys(errorData).length > 1) {
          const nestedErrorFiles = Object.fromEntries(
            Object.entries(errorData).filter(
              ([_, value]) => Object.keys(value as object).length > 0,
            ),
          );

          const errorMessage = Object.entries(nestedErrorFiles)
            .map(([filename, errors]) => {
              const errorMessage = Object.values(errors as object)[0];
              return `${filename} - ${errorMessage}`;
            })
            .join('\n');

          updateValidationStatus(
            receivedFiles[+errorFileIndex],
            'error',
            errorMessage,
          );
        } else {
          const errorValue = Object.values(errorData)[0];
          const defaultErrorMessage = intl.formatMessage({
            id: 'customer-platform.shared.components.forms.listing-form.filepicker.validation-failed',
          });
          updateValidationStatus(
            receivedFiles[+errorFileIndex],
            'error',
            typeof errorValue === 'object' &&
              errorValue &&
              Object.keys(errorValue).length === 0
              ? defaultErrorMessage
              : (errorValue as string),
          );
        }
      }
      const keysOfErroneousFiles = Object.keys(
        error.response.data[errorResponseFieldName],
      ).map(index => +index);
      const successfulFiles = receivedFiles.filter(
        (_, index) => !keysOfErroneousFiles.includes(index),
      );
      successfulFiles.forEach(file => updateValidationStatus(file, 'success'));
    } else {
      files.forEach(file =>
        updateValidationStatus(
          file,
          'error',
          intl.formatMessage({
            id: 'customer-platform.shared.generic-error',
          }),
        ),
      );
    }
  };

  const updateValidationStatus = (
    file: File,
    status: string,
    message?: string,
  ) => {
    setValidationStatus(prevValidationStatus => {
      const updatedValidationStatus = new Map(prevValidationStatus);
      updatedValidationStatus.set(getFileFootprint(file), {
        status: status,
        message: message || '',
      } as PhotoValidationStatus);
      return updatedValidationStatus;
    });
  };

  const fileMustBeValidated = useCallback(
    (file: File) => {
      return (
        !validationStatus.has(getFileFootprint(file)) &&
        Object.values(FILE_MANAGER_FORMATS_VALIDABLE).includes(file.type)
      );
    },
    [validationStatus],
  );

  const fileValidationFailed = useCallback(
    (file: File) => {
      return (
        validationStatus.has(getFileFootprint(file)) &&
        validationStatus.get(getFileFootprint(file))?.status === 'error'
      );
    },
    [validationStatus],
  );

  const getFileValidationErrorMessage = useCallback(
    (file: File): string | undefined => {
      return fileValidationFailed(file)
        ? validationStatus.get(getFileFootprint(file))?.message
        : undefined;
    },
    [fileValidationFailed, validationStatus],
  );

  const hasPendingValidations = useCallback(
    () =>
      Array.from(validationStatus.values()).some(
        entry => entry.status === 'pending',
      ),
    [validationStatus],
  );

  useEffect(() => {
    if (onValidationChange) {
      onValidationChange();
    }
    // We have to disable the exhaustive-deps rule here because including onValidationChange
    // would cause unnecessary re-renders that could clear error states prematurely.
    // The validation status map is the only dependency we need to track.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [validationStatus]);

  return {
    validateFiles,
    fileMustBeValidated,
    fileValidationFailed,
    getFileValidationErrorMessage,
    hasPendingValidations,
    validationStatus,
  };
}
